import {all, call, put, takeEvery} from '@redux-saga/core/effects';
import {push} from 'connected-react-router';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, Loaded, Loading} from 'Core/models/Async';
import {ValidEmail} from 'Core/models/Email';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {ValidSecurityQuestion} from 'Core/models/SecurityQuestion';
import {isRight, left} from 'fp-ts/lib/Either';
import {takeLatest} from 'redux-saga/effects';
import {refreshToken} from 'Shared/utilities/Auth';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {
  acceptTermsOfService,
  changeEmail,
  changeMobilePhone,
  changeNameInformation,
  changeSecurityQuestionForm,
  changeSecurityQuestions,
  changeUserProfile,
  getUserProfile,
  saveEmail,
  saveMobilePhone,
  saveNameInformation,
  saveSecurityQuestions,
  updateDefaultAgencyAssociation,
  UserAccountActionType,
} from 'Users/ducks/userAccount';
import {SavingNameInformation, UnchangedNameInformation} from 'Users/models/NameInformation';
import {UserProfilePayload} from 'Users/models/UserProfilePayload';
import {loadNewUserClaims} from 'Users/sagas/independentAgentSaga';

export function* loadUserProfile(action: ReturnType<typeof getUserProfile>) {
  const userProfileEither: AsyncResult<UserProfilePayload> = yield call(userApi.getUserProfile, action.payload.userId);

  if (isRight(userProfileEither)) {
    const userProfile = userProfileEither.right;

    yield put(changeUserProfile(new Loaded(userProfile)));

    yield put(changeEmail(new Loaded(left(ValidEmail.create(userProfile.email)))));

    yield put(changeMobilePhone(new Loaded(left(ValidPhoneNumber.create(userProfile.mobilePhone)))));

    if (userProfile.securityQuestions && userProfile.securityQuestions.length > 0) {
      yield put(
        changeSecurityQuestionForm(
          new Loaded(
            left({
              securityQuestionOne: ValidSecurityQuestion.create(
                userProfile.securityQuestions[0].id,
                userProfile.securityQuestions[0].response
              ),
              securityQuestionTwo: ValidSecurityQuestion.create(
                userProfile.securityQuestions[1].id,
                userProfile.securityQuestions[1].response
              ),
            })
          )
        )
      );
    }
  }
}

export function* getUserProfileSaga() {
  yield takeEvery(UserAccountActionType.GET_USER_PROFILE, loadUserProfile);
}

export function* acceptTerms(action: ReturnType<typeof acceptTermsOfService>) {
  yield call(userApi.acceptTermsAndConditions, action.payload.username);

  yield call(refreshToken);

  yield put(push(CoreRouteConstants.DASHBOARD));
}

export function* acceptTermsAndConditionsSaga() {
  yield takeEvery(UserAccountActionType.ACCEPT_TOS, acceptTerms);
}

export function* saveNameApi(action: ReturnType<typeof saveNameInformation>) {
  const {nameInformation, userId, encryptedIdentifier} = action.payload;
  yield put(changeNameInformation(new SavingNameInformation(nameInformation.firstName, nameInformation.lastName)));

  yield call(
    userApi.saveName,
    userId,
    nameInformation.firstName.value,
    nameInformation.lastName.value,
    encryptedIdentifier
  );

  yield call(refreshToken);

  yield call(loadNewUserClaims);

  yield put(changeNameInformation(new UnchangedNameInformation(nameInformation.firstName, nameInformation.lastName)));
}

export function* saveNameChangesSaga() {
  yield takeEvery(UserAccountActionType.SAVE_NAME_INFORMATION, saveNameApi);
}

export function* saveEmailApi(action: ReturnType<typeof saveEmail>) {
  const {email, encryptedIdentifier, userId} = action.payload;

  yield put(changeEmail(new Loading()));

  yield call(userApi.saveEmail, userId, email.address, encryptedIdentifier);

  yield call(refreshToken);

  yield call(loadNewUserClaims);

  yield put(changeEmail(new Loaded(left(email))));
}

export function* saveEmailChangesSaga() {
  yield takeEvery(UserAccountActionType.SAVE_EMAIL, saveEmailApi);
}

export function* saveMobilePhoneApi(action: ReturnType<typeof saveMobilePhone>) {
  const {phone, encryptedIdentifier, userId} = action.payload;

  yield put(changeMobilePhone(new Loading()));

  yield call(userApi.saveMobilePhone, userId, phone.phoneNumber, encryptedIdentifier);

  yield call(refreshToken);

  yield call(loadNewUserClaims);

  yield put(changeMobilePhone(new Loaded(left(phone))));
}

export function* saveMobilePhoneChangesSaga() {
  yield takeEvery(UserAccountActionType.SAVE_PHONE, saveMobilePhoneApi);
}

export function* getSecurityQuestionsSaga() {
  yield takeEvery(
    UserAccountActionType.GET_SECURITY_QUESTIONS,
    getApiCallGenerator(userApi.getSecurityQuestions, changeSecurityQuestions)
  );
}

export function* saveSecurityQuestionsApi(action: ReturnType<typeof saveSecurityQuestions>) {
  const {securityQuestionForm, encryptedIdentifier, userId} = action.payload;

  yield put(changeSecurityQuestionForm(new Loading()));

  yield call(
    userApi.saveSecurityQuestions,
    userId,
    securityQuestionForm.securityQuestionOne,
    securityQuestionForm.securityQuestionTwo,
    encryptedIdentifier
  );

  yield call(refreshToken);

  yield call(loadNewUserClaims);

  yield put(changeSecurityQuestionForm(new Loaded(left(securityQuestionForm))));
}

export function* saveSecurityQuestionSaga() {
  yield takeEvery(UserAccountActionType.SAVE_SECURITY_QUESTIONS, saveSecurityQuestionsApi);
}

function* updateDefaultAgencyAssociationApi(action: ReturnType<typeof updateDefaultAgencyAssociation>) {
  const {id, encryptedIdentifier, isNewYork} = action.payload;
  const result = yield call(userApi.updateDefaultAgencyAssociation, id, encryptedIdentifier, isNewYork);

  if (isRight(result)) {
    yield call(refreshToken);
    yield call(loadNewUserClaims);
    yield put(createSnackbar('Default Tax ID Updated Successfully.'));
  } else {
    yield put(createSnackbar('Unable to Default New Tax ID.'));
  }
}

export function* updateDefaultAgencyAssociationSaga() {
  yield takeLatest(UserAccountActionType.UPDATE_DEFAULT_AGENCY_ASSOCIATION, updateDefaultAgencyAssociationApi);
}

export default function* userSaga() {
  yield all([
    saveSecurityQuestionSaga(),
    acceptTermsAndConditionsSaga(),
    saveNameChangesSaga(),
    getUserProfileSaga(),
    saveEmailChangesSaga(),
    saveMobilePhoneChangesSaga(),
    getSecurityQuestionsSaga(),
    updateDefaultAgencyAssociationSaga(),
  ]);
}
